import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import Modal from '../../components/modal';
import { useStatus } from '../../context/StatusContext';
import { StatusEvaluator } from '../../context/StatusEvaluator.js';
import '../../styles/mobile/FTSOOverview.css';
import FTSOSummary from '../FTSOSummary';
import LoadingAnimation from '../../components/loadingAnimation';
import '../../styles/loading.css';
import '../../styles/components.css';
import {
  getHeaderStatus,
  getHeaderClass,
  getHeaderIcon,
  getPercentageClass,
  getCountClass,
  getMetricClass,
  calculateSubmissionsAverage,
  calculateResultsIssues,
  getIssuesColor
} from '../../utils/statusHelpers';
import { useDataUpdates } from '../../hooks/useDataUpdates';
import { getApiBase } from '../../utils/apiBase';
import { useDataCache } from '../../hooks/useDataCache';
import { cacheManager } from '../../hooks/GlobalCacheManager';

// Define summaryConfigs outside the component for a stable reference
const summaryConfigs = [
  { entity: 'ENV', network: 'flare', title: 'Envision Flare' },
  { entity: 'ENV', network: 'songbird', title: 'Envision Songbird' },
  { entity: 'UYS', network: 'flare', title: 'UYS Flare' },
  { entity: 'UYS', network: 'songbird', title: 'UYS Songbird' },
];

function FTSOOverview() {
  const [summaryData, setSummaryData] = useState({});
  const [resultsMetrics, setResultsMetrics] = useState({});
  const [registrationSummaryData, setRegistrationSummaryData] = useState({});
  const [dashboardGeneralData, setDashboardGeneralData] = useState({});
  const [hitRatesData, setHitRatesData] = useState({});
  const [latestResultsVR, setLatestResultsVR] = useState({});
  const [latestSummaryVR, setLatestSummaryVR] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { updatePageStatus } = useStatus();
  const [activeView, setActiveView] = useState('summary');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hitRatesScroller, setHitRatesScroller] = useState(null);
  
  const summaryWrapperRef = useRef(null);
  const prevActiveViewRef = useRef();
  const currentSlideRef = useRef(currentSlide);
  const isInitialLoadRef = useRef(true);
  const { getCachedData } = useDataCache();
  const lastRelevantTsRef = useRef({});
  const lastDashTsRef = useRef(null);
  const lastSummaryTsRef = useRef(null);
  const lastResultsTsRef = useRef(null);
  const lastCountsTsRef = useRef(null);
  const lastPivotTsRef = useRef(null);

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: null
  });
  const [sectionsState, setSectionsState] = useState({
    submissions: true,
    results: true,
  });

  const API = getApiBase();
  const FTSO_PERFORMANCE_ENDPOINT = `${API}/ftso_performance_dashboard.json`;
  const SUBMISSION_SUMMARY_ENDPOINT = `${API}/submission_summary.json`;

  const handleScrollerReady = useCallback((scrollerElement) => {
    console.log("FTSOOverview: FTSOSummary scroller is ready:", scrollerElement);
    setHitRatesScroller(scrollerElement);
  }, []);

  const handleScrollerDestroyed = useCallback(() => {
    console.log("FTSOOverview: FTSOSummary scroller is destroyed.");
    setHitRatesScroller(null);
  }, []);

  useEffect(() => {
    prevActiveViewRef.current = activeView;
  });

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  const handleScroll = useCallback((event) => {
    const scroller = event.target;
    if (!scroller) return;

    let firstSlideElement;
    let effectiveSlideWidth;
    let cardWidthFromElement = 0;
    let calculatedGap = 0;
    const localSummaryConfigsLength = summaryConfigs.length;

    if (activeView === 'summary') {
      if (scroller.children.length > 0) {
        firstSlideElement = scroller.children[0];
        cardWidthFromElement = firstSlideElement.offsetWidth;
        calculatedGap = 20;
        effectiveSlideWidth = cardWidthFromElement + calculatedGap;
      } else {
        return;
      }
    } else if (activeView === 'hitrates') {
      if (scroller.children.length > 0 && scroller.children[0].children.length > 0) {
        firstSlideElement = scroller.children[0].children[0];
        cardWidthFromElement = firstSlideElement.offsetWidth;
        calculatedGap = 20;
        effectiveSlideWidth = cardWidthFromElement + calculatedGap;
      } else {
        return;
      }
    }

    if (!firstSlideElement || effectiveSlideWidth === undefined || effectiveSlideWidth === 0) {
      return;
    }

    const scrollPosition = scroller.scrollLeft;
    let newSlide;
    if (activeView === 'hitrates') {
        newSlide = Math.round(scrollPosition / effectiveSlideWidth);
    } else {
        newSlide = Math.round((scrollPosition + (effectiveSlideWidth * 0.1)) / effectiveSlideWidth);
    }

    setCurrentSlide(prevCurrentSlide => {
      if (newSlide !== prevCurrentSlide) {
        if (newSlide >= 0 && newSlide < localSummaryConfigsLength) {
          return newSlide;
        } else {
          return prevCurrentSlide;
        }
      }
      return prevCurrentSlide;
    });
  }, [activeView, summaryConfigs.length]);

  const handleDataUpdate = useCallback(async () => {
    if (isInitialLoadRef.current) {
      setLoading(true);
    }
    setError(null);

    console.log('FTSOOverview handleDataUpdate: consuming cache if available');
    try {
      // Prefer cached data populated by SSE prefetcher
      const cachedDash = getCachedData('/ftso_performance_dashboard.json');
      const cachedSummary = getCachedData('/submission_summary.json');
      const cachedCounts = getCachedData('/feed_counts.json');
      const cachedPivot = getCachedData('/pivot_voting_round.json');

      // Render immediately if dashboard is cached; use other cached artifacts when present
      if (cachedDash) {
        const dashboardData = cachedDash;
        const submissionSummary = cachedSummary || {};
        const feedCountsData = cachedCounts || null;
        console.log('Using cached dashboard for immediate render in FTSOOverview');

        const newSummaryData = {};
        const newRegistrationSummaryData = {};
        const newDashboardGeneralData = {};
        const newHitRatesData = {};
        const newResultsMetrics = {};
        // Prefer dashboard-supplied results metrics to avoid extra computation
        let overallPageStatusForDisplay = 'grey';

        Object.keys(dashboardData).forEach(entityKey => {
          if (entityKey === "metadata") return;
          newSummaryData[entityKey] = {};
          newRegistrationSummaryData[entityKey] = {};
          newDashboardGeneralData[entityKey] = {};
          newHitRatesData[entityKey] = {};

          Object.keys(dashboardData[entityKey]).forEach(networkKey => {
            const entityNetworkData = dashboardData[entityKey][networkKey];
            const summaryEN = submissionSummary?.[entityKey]?.[networkKey] || {};
            const out = {};
            ['submit1', 'submit2', 'submitSignatures'].forEach(method => {
              out[method] = {};
              ['15min', '2hr', '6hr'].forEach(tf => {
                const pct = summaryEN?.[method]?.[tf]?.percentage;
                if (typeof pct === 'number') out[method][tf] = { percentage: pct };
              });
            });
            newSummaryData[entityKey][networkKey] = out;
            newRegistrationSummaryData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.voter_registration || {};
            newDashboardGeneralData[entityKey][networkKey] = entityNetworkData?.general || {};
            newHitRatesData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.hit_rates || {};
            const rm = entityNetworkData?.detailed_metrics?.results_metrics;
            if (rm && typeof rm === 'object') {
              if (!newResultsMetrics[entityKey]) newResultsMetrics[entityKey] = {};
              newResultsMetrics[entityKey][networkKey] = rm;
            }
            if (entityKey.toUpperCase() === 'UYS' && networkKey.toLowerCase() === 'flare') {
              overallPageStatusForDisplay = entityNetworkData?.detailed_metrics?.overall_page_status || 'grey';
            }
          });
        });

        setSummaryData(newSummaryData);
        if (Object.keys(newResultsMetrics).length > 0) {
          setResultsMetrics(newResultsMetrics);
        }
        setRegistrationSummaryData(newRegistrationSummaryData);
        setDashboardGeneralData(newDashboardGeneralData);
        setHitRatesData(newHitRatesData);
        if (!cacheManager.isRefreshing() || overallPageStatusForDisplay === 'green') {
          updatePageStatus('ftsoOverview', overallPageStatusForDisplay);
        }
        setLoading(false);
        isInitialLoadRef.current = false;
        return;
      }
    } catch {}

    // Cold cache fallback: fetch from network
    try {
      const timestamp = Date.now();
      // Request without cache-busting to allow conditional requests (ETag/Last-Modified)
      const [dashboardResponse, summaryResponse] = await Promise.all([
        fetch(`${FTSO_PERFORMANCE_ENDPOINT}`),
        fetch(`${SUBMISSION_SUMMARY_ENDPOINT}`)
      ]);
      if (!dashboardResponse.ok) {
        throw new Error(`HTTP error! status: ${dashboardResponse.status} for ${FTSO_PERFORMANCE_ENDPOINT}`);
      }
      const dashboardData = await dashboardResponse.json();
      const submissionSummary = summaryResponse.ok ? await summaryResponse.json() : {};
      // results/feeds/pivot no longer needed for results metrics in mobile

      // Reuse existing processing block
      const newSummaryData = {};
      const newRegistrationSummaryData = {};
      const newDashboardGeneralData = {};
      const newHitRatesData = {};
      let overallPageStatusForDisplay = 'grey';

      Object.keys(dashboardData).forEach(entityKey => {
        if (entityKey === "metadata") return;
        newSummaryData[entityKey] = {};
        newRegistrationSummaryData[entityKey] = {};
        newDashboardGeneralData[entityKey] = {};
        newHitRatesData[entityKey] = {};

        Object.keys(dashboardData[entityKey]).forEach(networkKey => {
          const entityNetworkData = dashboardData[entityKey][networkKey];
          const summaryEN = submissionSummary?.[entityKey]?.[networkKey] || {};
          const out = {};
          ['submit1', 'submit2', 'submitSignatures'].forEach(method => {
            out[method] = {};
            ['15min', '2hr', '6hr'].forEach(tf => {
              const pct = summaryEN?.[method]?.[tf]?.percentage;
              if (typeof pct === 'number') out[method][tf] = { percentage: pct };
            });
          });
          newSummaryData[entityKey][networkKey] = out;
          newRegistrationSummaryData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.voter_registration || {};
          newDashboardGeneralData[entityKey][networkKey] = entityNetworkData?.general || {};
          newHitRatesData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.hit_rates || {};
          if (entityKey.toUpperCase() === 'UYS' && networkKey.toLowerCase() === 'flare') {
            overallPageStatusForDisplay = entityNetworkData?.detailed_metrics?.overall_page_status || 'grey';
          }
        });
      });

        // Removed expensive submit2prices-based recomputation; rely on dashboard metrics only
      setSummaryData(newSummaryData);
      // Build results metrics directly from dashboard
      const rmAll = {};
      Object.keys(dashboardData || {}).forEach(entityKey => {
        if (entityKey === 'metadata') return;
        Object.keys(dashboardData[entityKey] || {}).forEach(networkKey => {
          const rm = dashboardData?.[entityKey]?.[networkKey]?.detailed_metrics?.results_metrics;
          if (rm && typeof rm === 'object') {
            if (!rmAll[entityKey]) rmAll[entityKey] = {};
            rmAll[entityKey][networkKey] = rm;
          }
        });
      });
      setResultsMetrics(rmAll);
      setRegistrationSummaryData(newRegistrationSummaryData);
      setDashboardGeneralData(newDashboardGeneralData);
      setHitRatesData(newHitRatesData);
      if (!cacheManager.isRefreshing() || overallPageStatusForDisplay === 'green') {
        updatePageStatus('ftsoOverview', overallPageStatusForDisplay);
      }
    } catch (error) {
      console.error('Error in FTSOOverview handleDataUpdate (cold fetch):', error);
      setError(error.message);
      if (!cacheManager.isRefreshing()) {
        updatePageStatus('ftsoOverview', 'red');
      }
    } finally {
      setLoading(false);
      isInitialLoadRef.current = false;
    }
  }, [updatePageStatus, FTSO_PERFORMANCE_ENDPOINT, getCachedData, API]);

  // On SSE, consume cache and update state using whatever is available
  const consumeCacheOnSse = useCallback(async () => {
    try {
      const cachedDash = getCachedData('/ftso_performance_dashboard.json');
      const cachedSummary = getCachedData('/submission_summary.json');
      if (!cachedDash && !cachedSummary) return;

      const dashboardData = cachedDash || {};
      const submissionSummary = cachedSummary || {};
      // results/feeds not required for results metrics here

      const nextSummaryData = {};
      const nextRegistrationSummaryData = {};
      const nextDashboardGeneralData = {};
      const nextHitRatesData = {};
      let nextOverallStatus = 'grey';
      Object.keys(dashboardData).forEach(entityKey => {
        if (entityKey === 'metadata') return;
        nextSummaryData[entityKey] = {};
        nextRegistrationSummaryData[entityKey] = {};
        nextDashboardGeneralData[entityKey] = {};
        nextHitRatesData[entityKey] = {};
        Object.keys(dashboardData[entityKey]).forEach(networkKey => {
          const entityNetworkData = dashboardData[entityKey][networkKey];
          const summaryEN = submissionSummary?.[entityKey]?.[networkKey] || {};
          const out = {};
          ['submit1', 'submit2', 'submitSignatures'].forEach(method => {
            out[method] = {};
            ['15min', '2hr', '6hr'].forEach(tf => {
              const pct = summaryEN?.[method]?.[tf]?.percentage;
              if (typeof pct === 'number') out[method][tf] = { percentage: pct };
            });
          });
          nextSummaryData[entityKey][networkKey] = out;
          nextRegistrationSummaryData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.voter_registration || {};
          nextDashboardGeneralData[entityKey][networkKey] = entityNetworkData?.general || {};
          nextHitRatesData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.hit_rates || {};
          if (entityKey.toUpperCase() === 'UYS' && networkKey.toLowerCase() === 'flare') {
            nextOverallStatus = entityNetworkData?.detailed_metrics?.overall_page_status || 'grey';
          }
        });
      });
      // Prefer dashboard results metrics
      let nextResultsMetrics = {};
      try {
        Object.keys(dashboardData || {}).forEach(entityKey => {
          if (entityKey === 'metadata') return;
          Object.keys(dashboardData[entityKey] || {}).forEach(networkKey => {
            const rm = dashboardData?.[entityKey]?.[networkKey]?.detailed_metrics?.results_metrics;
            if (rm && typeof rm === 'object') {
              if (!nextResultsMetrics[entityKey]) nextResultsMetrics[entityKey] = {};
              nextResultsMetrics[entityKey][networkKey] = rm;
            }
          });
        });
      } catch {}

      // Update latest results VR map from dashboard general data
      try {
        const vrMap = {};
        Object.keys(dashboardData || {}).forEach(entity => {
          if (entity === 'metadata') return;
          vrMap[entity] = vrMap[entity] || {};
          Object.keys(dashboardData[entity] || {}).forEach(network => {
            const val = Number(dashboardData?.[entity]?.[network]?.general?.latest_results_voting_round || 0);
            vrMap[entity][network] = val;
          });
        });
        setLatestResultsVR(vrMap);
      } catch {}

      // Update latest summary VR map
      try {
        const sumCache = getCachedData('/submission_summary.json');
        if (sumCache && typeof sumCache === 'object') {
          const vrMap = {};
          Object.keys(sumCache || {}).forEach(entity => {
            if (entity === 'metadata') return;
            vrMap[entity] = vrMap[entity] || {};
            Object.keys(sumCache[entity] || {}).forEach(network => {
              vrMap[entity][network] = Number(sumCache[entity][network]?.latest_voting_round || 0);
            });
          });
          setLatestSummaryVR(vrMap);
        }
      } catch {}

      // If VR advanced but hit_rates stayed unchanged, recompute from cached artifacts and override
      try {
        const summaryJson2 = getCachedData('/submission_summary.json');
        const pricesJson2 = getCachedData('/submit2prices.json');
        const feedCfgJson2 = getCachedData('/feed_config.json');
        if (summaryJson2 && pricesJson2 && feedCfgJson2) {
          Object.keys(nextDashboardGeneralData || {}).forEach(entity => {
            Object.keys(nextDashboardGeneralData[entity] || {}).forEach(network => {
              const latestNow = Number(nextDashboardGeneralData[entity]?.[network]?.latest_processed_voting_round || 0);
              const latestPrev = Number(dashboardGeneralData?.[entity]?.[network]?.latest_processed_voting_round || 0);
              const hrNow = nextHitRatesData[entity]?.[network] || {};
              const hrPrev = hitRatesData?.[entity]?.[network] || {};
              const unchanged = ['15min','2hr','6hr'].every(tf => {
                const a = hrNow?.[tf]?.hitRate; const b = hrPrev?.[tf]?.hitRate;
                return typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) < 0.0001;
              });
              if (latestNow > latestPrev && unchanged) {
                const compute = (tfKey) => {
                  try {
                    const sumEN = summaryJson2?.[entity]?.[network] || {};
                    const latest = Number(sumEN.latest_voting_round || 0);
                    const start = Number(sumEN[`${tfKey}_last_voting_round`] || 0);
                    if (!latest || !start) return undefined;
                    const cfg = (feedCfgJson2?.[entity]?.[network] || []).filter(f => f?.submit === true);
                    const expected = cfg.map(f => String(f.feed_name || '').trim().toLowerCase()).filter(Boolean);
                    if (!expected.length) return undefined;
                    let totalExp = 0; let totalRes = 0;
                    for (let vr = start + 1; vr <= latest; vr++) {
                      const list = pricesJson2?.[entity]?.[network]?.[String(vr)] || [];
                      const map = {};
                      if (Array.isArray(list)) {
                        list.forEach(it => { const k = String(it?.feed || '').trim().toLowerCase(); if (k) map[k] = Number(it?.result || 0); });
                      }
                      expected.forEach(k => { totalExp += 1; totalRes += Number(map[k] || 0); });
                    }
                    return totalExp > 0 ? Math.round((totalRes / totalExp) * 10000) / 100 : undefined;
                  } catch { return undefined; }
                };
                ['15min','2hr','6hr'].forEach(tf => {
                  const hr = compute(tf);
                  if (typeof hr === 'number') {
                    if (!nextHitRatesData[entity]) nextHitRatesData[entity] = {};
                    if (!nextHitRatesData[entity][network]) nextHitRatesData[entity][network] = {};
                    nextHitRatesData[entity][network][tf] = { hitRate: hr };
                  }
                });
              }
            });
          });
        }
      } catch {}

      const isEqual = (a, b) => {
        try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
      };

      let changed = false;
      if (!isEqual(summaryData, nextSummaryData)) { setSummaryData(nextSummaryData); changed = true; }
      if (!isEqual(registrationSummaryData, nextRegistrationSummaryData)) { setRegistrationSummaryData(nextRegistrationSummaryData); changed = true; }
      if (!isEqual(dashboardGeneralData, nextDashboardGeneralData)) { setDashboardGeneralData(nextDashboardGeneralData); changed = true; }
      if (!isEqual(hitRatesData, nextHitRatesData)) { setHitRatesData(nextHitRatesData); changed = true; }
      if (!isEqual(resultsMetrics, nextResultsMetrics)) { setResultsMetrics(nextResultsMetrics); changed = true; }
      if (changed) {
        if (!cacheManager.isRefreshing() || nextOverallStatus === 'green') {
          updatePageStatus('ftsoOverview', nextOverallStatus);
        }
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    } catch {}
  }, [getCachedData, summaryData, registrationSummaryData, dashboardGeneralData, hitRatesData, resultsMetrics, updatePageStatus]);

  // Align with desktop behavior: check export_timestamps and update only when relevant artifacts changed.
  const checkAndFetchOnSse = useCallback(async () => {
    try {
      const tsResp = await fetch(`${API}/export_timestamps.json`);
      if (!tsResp.ok) return;
      const tsJson = await tsResp.json();
      const dashTs = tsJson?.['ftso_performance_dashboard.json']?.last_updated || null;
      const sumTs = tsJson?.['submission_summary.json']?.last_updated || null;
      const resTs = null; // not used in mobile metrics
      const cntTs = null; // not used in mobile metrics

      const anyChanged = (
        (dashTs && dashTs !== lastDashTsRef.current) ||
        (sumTs && sumTs !== lastSummaryTsRef.current) ||
        false
      );
      if (!anyChanged) return;

      lastDashTsRef.current = dashTs || lastDashTsRef.current;
      lastSummaryTsRef.current = sumTs || lastSummaryTsRef.current;
      // results/feeds/pivot no longer drive updates

      // Allow prefetcher to hydrate cache coherently, then update once to avoid mismatched VR/percentages
      await new Promise(r => setTimeout(r, 200));
      await handleDataUpdate();
    } catch {}
  }, [API, getCachedData, consumeCacheOnSse, handleDataUpdate]);

  useDataUpdates(checkAndFetchOnSse);

  useEffect(() => {
    console.log('FTSOOverview component mounted, performing initial data fetch');
    cacheManager.registerEndpoints('mobileFtsoOverview', [
      '/ftso_performance_dashboard.json',
      '/submission_summary.json',
      '/feed_counts.json',
      '/pivot_voting_round.json',
      '/feed_config.json'
    ]);
    try {
      const cachedDash = getCachedData('/ftso_performance_dashboard.json');
      const cachedSummary = getCachedData('/submission_summary.json');
      const cachedCounts = getCachedData('/feed_counts.json');
      const cachedPivot = getCachedData('/pivot_voting_round.json');
      if (cachedDash) {
        // Render immediately from cache (even if other artifacts are missing) to avoid spinner
        const dashboardData = cachedDash;
        const submissionSummary = cachedSummary || {};
        const feedCountsData = cachedCounts || null;

        const newSummaryData = {};
        const newRegistrationSummaryData = {};
        const newDashboardGeneralData = {};
        const newHitRatesData = {};
        let overallPageStatusForDisplay = 'grey';
        Object.keys(dashboardData).forEach(entityKey => {
          if (entityKey === 'metadata') return;
          newSummaryData[entityKey] = {};
          newRegistrationSummaryData[entityKey] = {};
          newDashboardGeneralData[entityKey] = {};
          newHitRatesData[entityKey] = {};
          Object.keys(dashboardData[entityKey]).forEach(networkKey => {
            const entityNetworkData = dashboardData[entityKey][networkKey];
            const summaryEN = submissionSummary?.[entityKey]?.[networkKey] || {};
            const out = {};
            ['submit1', 'submit2', 'submitSignatures'].forEach(method => {
              out[method] = {};
              ['15min', '2hr', '6hr'].forEach(tf => {
                const pct = summaryEN?.[method]?.[tf]?.percentage;
                if (typeof pct === 'number') out[method][tf] = { percentage: pct };
              });
            });
            newSummaryData[entityKey][networkKey] = out;
            newRegistrationSummaryData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.voter_registration || {};
            newDashboardGeneralData[entityKey][networkKey] = entityNetworkData?.general || {};
            newHitRatesData[entityKey][networkKey] = entityNetworkData?.detailed_metrics?.hit_rates || {};
            if (entityKey.toUpperCase() === 'UYS' && networkKey.toLowerCase() === 'flare') {
              overallPageStatusForDisplay = entityNetworkData?.detailed_metrics?.overall_page_status || 'grey';
            }
          });
        });
        setSummaryData(newSummaryData);
        setRegistrationSummaryData(newRegistrationSummaryData);
        setDashboardGeneralData(newDashboardGeneralData);
        setHitRatesData(newHitRatesData);
        setLoading(false);
      }
    } catch {}
    handleDataUpdate()
      .finally(() => {
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          console.log('FTSOOverview initial load process complete, isInitialLoadRef set to false.');
        }
      });
    
    return () => {
      console.log('FTSOOverview component unmounting, cleaning up state');
      setSummaryData({});
      setResultsMetrics({});
      setRegistrationSummaryData({});
      setDashboardGeneralData({});
      setHitRatesData({});
      setError(null);
      isInitialLoadRef.current = true;
      cacheManager.unregisterEndpoints('mobileFtsoOverview');
    };
  }, [handleDataUpdate]);

  useLayoutEffect(() => {
    const didActiveViewChange = prevActiveViewRef.current !== activeView;

    if (didActiveViewChange) {
      setCurrentSlide(0);

      if (activeView === 'summary') {
        if (summaryWrapperRef.current) {
          summaryWrapperRef.current.scrollLeft = 0;
        }
      } else if (activeView === 'hitrates') {
        if (hitRatesScroller) {
          hitRatesScroller.scrollLeft = 0;
        }
      }
    }
  }, [activeView, hitRatesScroller]);

  useLayoutEffect(() => {
    let scrollerToUse = null;

    if (activeView === 'summary') {
      if (summaryWrapperRef.current) {
        scrollerToUse = summaryWrapperRef.current;
      }
    } else if (activeView === 'hitrates') {
      if (hitRatesScroller) { 
        scrollerToUse = hitRatesScroller;
      }
    }

    if (scrollerToUse) {
      scrollerToUse.addEventListener('scroll', handleScroll);
      return () => {
        scrollerToUse.removeEventListener('scroll', handleScroll);
      };
    }
  }, [activeView, handleScroll, hitRatesScroller]);

  if (loading && isInitialLoadRef.current) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div className="error-message-centered">Error loading FTSO Overview data: {error}</div>;
  }

  const toggleSection = (section) => {
    setSectionsState(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleZeroSubsClick = (entity, network, timeframe, zeroRounds) => {
    setModalState({
      isOpen: true,
      title: `Zero Submissions - ${entity} ${network} (${timeframe})`,
      content: (
        <div className="mobile-modal-content">
          <p>Voting rounds with zero submissions:</p>
          <div className="mobile-rounds-grid">
            {zeroRounds.map(round => (
              <div key={round} className="round-item">
                {round}
              </div>
            ))}
          </div>
        </div>
      )
    });
  };

  const handleRevealOffendersClick = (entity, network, timeframe, offendersList, currentVotingRound) => {
    const timeframeRounds = {
      '15min': 10,
      '2hr': 80,
      '6hr': 240
    };
    const minRound = currentVotingRound - timeframeRounds[timeframe];
    const relevantOffenders = offendersList.filter(vr => vr >= minRound);
  
    setModalState({
      isOpen: true,
      title: `Reveal Offenders - ${entity} ${network} (${timeframe})`,
      content: (
        <div className="mobile-modal-content">
          <p>Voting rounds with reveal offenders:</p>
          <div className="mobile-rounds-grid">
            {relevantOffenders.map(round => (
              <div key={round} className="round-item">
                {round}
              </div>
            ))}
          </div>
        </div>
      )
    });
  };

  return (
    <div className="mobile-wrapper ftso-page">
      <div className="mobile-overview">
        <div className="view-toggle-container">
          <span className={`toggle-label ${activeView === 'summary' ? 'active' : ''}`}>
            Summary
          </span>
          <div className="toggle-switch">
            <input 
              type="checkbox" 
              id="view-toggle" 
              checked={activeView === 'hitrates'}
              onChange={() => {
                const newView = activeView === 'summary' ? 'hitrates' : 'summary';
                setActiveView(newView);
                setCurrentSlide(0);
              }}
            />
            <label htmlFor="view-toggle" />
          </div>
          <span className={`toggle-label ${activeView === 'hitrates' ? 'active' : ''}`}>
            Hit Rates
          </span>
        </div>
    
        {activeView === 'summary' ? (
          <div key="summary-scroller" className="mobile-summary-wrapper" ref={summaryWrapperRef}>
              {summaryConfigs.map(({ entity, network, title }) => {
                const entityData = summaryData[entity]?.[network];
                const metricsData = resultsMetrics[entity]?.[network];
                const registrationData = registrationSummaryData[entity]?.[network];
                const generalData = dashboardGeneralData?.[entity]?.[network];
                // Render card as soon as submissions (entityData) are ready; do not block on results metrics
                const isCardLoading = loading || !entityData;

                const headerStatus = getHeaderStatus(entityData);
                // Only show VR when results for that VR exist; else show last VR with results
                const latestVR = Number(generalData?.latest_processed_voting_round || 0);
                let fallbackVR = 0;
                try {
                  const sumCache = getCachedData('/submission_summary.json');
                  fallbackVR = Number(sumCache?.[entity]?.[network]?.latest_voting_round || 0);
                } catch {}
                const resultsLatestForPair = Number(latestResultsVR?.[entity]?.[network] || 0);
                const summaryLatestForPair = Number(latestSummaryVR?.[entity]?.[network] || 0);
                const gatedLatest = latestVR && summaryLatestForPair ? Math.min(latestVR, summaryLatestForPair) : (latestVR || summaryLatestForPair || 0);
                const resultsHaveGated = resultsLatestForPair && gatedLatest && resultsLatestForPair >= gatedLatest;
                const latestRoundForDisplay = (resultsHaveGated && gatedLatest) ? gatedLatest : (summaryLatestForPair || fallbackVR || 'N/A');
                
                return (
                  <div key={`${entity}-${network}`} className="mobile-summary-box">
                    <div className="header-section">
                      <div className="title-group">
                        <h2 className={getHeaderClass(headerStatus)}>
                          <div className="summary-box-title-group-mobile">
                            <span>{title}</span>
                            <span className="ftso-overview-latest-round">
                              ({latestRoundForDisplay})
                            </span>
                          </div>
                          <img
                            src={getHeaderIcon(headerStatus)}
                            alt="Status Icon"
                            className="header-icon"
                          />
                        </h2>
                      </div>
                    </div>
        
                    {isCardLoading && (
                      <div className="mobile-loading-placeholder card-loading-placeholder">Loading details...</div>
                    )}
                    {error && !isCardLoading && <div className="mobile-error-message">Error loading details.</div>}

                    {!isCardLoading && !error && (
                    <div className="summary-content">
                      {/* Submissions Section */}
                      <div className="section-header" onClick={() => toggleSection('submissions')}>
                          <span className="section-title">SUBMISSIONS</span>
                          {entityData ? (
                            <span className={`section-value ${getPercentageClass(
                                calculateSubmissionsAverage(entityData)
                            )}`}>
                                {calculateSubmissionsAverage(entityData)}%
                            </span>
                            ) : (
                              <span className="section-value-loading">N/A</span>
                            )}
                      </div>
        
                      {sectionsState.submissions && (
                        <div className="summary-grid">
                          <span className="grid-header"></span>
                          <span className="grid-header">15min</span>
                          <span className="grid-header">2hr</span>
                          <span className="grid-header">6hr</span>
        
                          {/* Submit1 row */}
                          <span className="method-label">submit1:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const pct = entityData?.submit1?.[timeframe]?.percentage;
                            const display = typeof pct === 'number' ? `${pct}%` : '-';
                            return (
                              <span
                                key={`submit1-${timeframe}`}
                                className={`percentage ${getPercentageClass(typeof pct === 'number' ? pct : -1)}`}
                              >
                                {display}
                              </span>
                            );
                          })}
        
                          {/* Submit2 row */}
                          <span className="method-label">submit2:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const pct = entityData?.submit2?.[timeframe]?.percentage;
                            const display = typeof pct === 'number' ? `${pct}%` : '-';
                            return (
                              <span
                                key={`submit2-${timeframe}`}
                                className={`percentage ${getPercentageClass(typeof pct === 'number' ? pct : -1)}`}
                              >
                                {display}
                              </span>
                            );
                          })}
        
                          {/* Signatures row */}
                          <span className="method-label">signatures:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const pct = entityData?.submitSignatures?.[timeframe]?.percentage;
                            const display = typeof pct === 'number' ? `${pct}%` : '-';
                            return (
                              <span
                                key={`signatures-${timeframe}`}
                                className={`percentage ${getPercentageClass(typeof pct === 'number' ? pct : -1)}`}
                              >
                                {display}
                              </span>
                            );
                          })}
        
                          {/* Zero Submissions row */}
                          <span className="method-label">zero subs:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const zeroCountRaw =
                              entityData?.submit2?.[timeframe]?.zero_count ??
                              entityData?.submit2?.[timeframe]?.zero_submissions_count;
                            const zeroRounds =
                              entityData?.submit2?.[timeframe]?.zero_rounds ??
                              entityData?.submit2?.[timeframe]?.zero_submission_rounds ?? [];
                            const zeroCount = typeof zeroCountRaw === 'number' ? zeroCountRaw : 0;
                            const hasRecords = zeroCount > 0;
                            return (
                              <span
                                key={`zero-${timeframe}`}
                                className={`percentage ${getCountClass(zeroCount)} ${hasRecords ? 'clickable' : ''}`}
                                onClick={hasRecords ? () => handleZeroSubsClick(
                                  entity,
                                  network,
                                  timeframe,
                                  zeroRounds
                                ) : undefined}
                              >
                                {zeroCount}
                              </span>
                            );
                          })}
                        </div>
                      )}
        
                      {/* Results Section */}
                      <div className="section-header" onClick={() => toggleSection('results')}>
                          <span className="section-title">RESULTS</span>
                          {metricsData ? (
                            <span className={`section-value ${getIssuesColor(
                                calculateResultsIssues(metricsData)
                            )}`}>
                                {calculateResultsIssues(metricsData)} issues
                            </span>
                             ) : (
                              <span className="section-value-loading">N/A</span>
                            )}
                      </div>
        
                      {sectionsState.results && (
                        <div className="summary-grid">
                          <span className="grid-header"></span>
                          <span className="grid-header">15min</span>
                          <span className="grid-header">2hr</span>
                          <span className="grid-header">6hr</span>
        
                          {/* Late row */}
                          <span className="method-label">late:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const tf = metricsData?.[timeframe] || {};
                            const value = tf.late_rounds ?? tf.late;
                            const total = tf.total_rounds_in_window ?? tf.total ?? 1;
                            return (
                              <span
                                key={`late-${timeframe}`}
                                className={`percentage ${getMetricClass(value === undefined ? -1 : value, total)}`}
                              >
                                {value === undefined ? '-' : value}
                              </span>
                            );
                          })}
        
                          {/* Missing row */}
                          <span className="method-label">missing:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const tf = metricsData?.[timeframe] || {};
                            const value = tf.missing_rounds ?? tf.missing;
                            const total = tf.total_rounds_in_window ?? tf.total ?? 1;
                            return (
                              <span
                                key={`missing-${timeframe}`}
                                className={`percentage ${getMetricClass(value === undefined ? -1 : value, total)}`}
                              >
                                {value === undefined ? '-' : value}
                              </span>
                            );
                          })}
        
                          {/* Invalid row */}
                          <span className="method-label">invalid:</span>
                          {['15min', '2hr', '6hr'].map((timeframe) => {
                            const tf = metricsData?.[timeframe] || {};
                            const value = tf.invalid_rounds ?? tf.invalid;
                            const total = tf.total_rounds_in_window ?? tf.total ?? 1;
                            return (
                              <span
                                key={`invalid-${timeframe}`}
                                className={`percentage ${getMetricClass(value === undefined ? -1 : value, total)}`}
                              >
                                {value === undefined ? '-' : value}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    )}
                  </div>
                );
              })}
            </div>
        ) : (
          <div key="hitrates-scroller-outer" className="mobile-summary-wrapper">
            <div className="mobile-summary-box ftso-hitrates-card">
              <FTSOSummary 
                key="ftso-summary-hitrates-instance"
                displayOrder={summaryConfigs}
                onScrollerReady={handleScrollerReady}
                onScrollerDestroyed={handleScrollerDestroyed}
              />
            </div>
          </div>
        )}
    
        <div className="pagination-dots-container mobile-only-pagination">
          {Array.from({ length: summaryConfigs.length }).map((_, index) => (
            <div 
              key={index} 
              className={`dot ${index === currentSlide ? 'active' : ''}`}
            />
          ))}
        </div>
    
        <Modal
          isOpen={modalState.isOpen}
          onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
          title={modalState.title}
        >
          {modalState.content}
        </Modal>
      </div>
    </div>
  );
}

export default FTSOOverview;